import React, { useState, useMemo, useEffect } from 'react';
import { useOutletContext, useLocation } from 'react-router-dom';
import { MdEventNote, MdSearch, MdCheckCircle, MdCancel, MdAccessTime, MdPerson, MdFreeBreakfast, MdDelete } from 'react-icons/md';
import { api } from '../services/api';
import Toast from '../components/Toast';
import { getLocalDateInputValue } from '../utils/dateUtils';
import { toTitleCase } from '../utils/dateUtils';

const navArrowBtn = {
  fontFamily: 'inherit',
  width: 28, height: 28, borderRadius: 8,
  border: '1px solid #E5E5EA', background: '#FFFFFF', color: '#6E6E73',
  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  padding: 0,
};

export default function Agenda() {
  const { currentUser, patients = [] } = useOutletContext();
  const location = useLocation();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchQuery, setSearchQuery] = useState('');
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showModal, setShowModal] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [showStatusMenu, setShowStatusMenu] = useState(null);
  const [settings, setSettings] = useState(null);
  const [appointmentForm, setAppointmentForm] = useState({
    date: getLocalDateInputValue(),
    time: '09:00',
    patientId: '',
    type: 'Consulta'
  });

  // Carregar configurações
  useEffect(() => {
    loadSettings();
  }, []);

  // Carregar agendamentos ao montar e sempre que navegar para esta tela
  useEffect(() => {
    loadAppointments();
  }, [location.key]);

  const loadSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    }
  };

  // Fechar menu de status ao clicar fora
  useEffect(() => {
    const handleClickOutside = () => {
      if (showStatusMenu) {
        setShowStatusMenu(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showStatusMenu]);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/appointments');
      setAppointments(response.data);
    } catch (error) {
      console.error('Erro ao carregar agendamentos:', error);
      setToast({ message: 'Erro ao carregar agendamentos', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Gerar slots de horário baseado nas configurações
  const timeSlots = useMemo(() => {
    if (!settings?.scheduleSettings) {
      // Se não tem configurações, usar horários padrão
      return [
        { time: '08:00', isBreak: false },
        { time: '08:30', isBreak: false },
        { time: '09:00', isBreak: false },
        { time: '09:30', isBreak: false },
        { time: '10:00', isBreak: false },
        { time: '10:30', isBreak: false },
        { time: '11:00', isBreak: false },
        { time: '11:30', isBreak: false },
        { time: '13:00', isBreak: false },
        { time: '13:30', isBreak: false },
        { time: '14:00', isBreak: false },
        { time: '14:30', isBreak: false },
        { time: '15:00', isBreak: false },
        { time: '15:30', isBreak: false },
        { time: '16:00', isBreak: false },
        { time: '16:30', isBreak: false },
        { time: '17:00', isBreak: false },
        { time: '17:30', isBreak: false }
      ];
    }

    const dayOfWeek = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'][selectedDate.getDay()];
    const dayConfig = settings.scheduleSettings.workDays?.[dayOfWeek];

    // Se o dia não está habilitado, retornar array vazio
    if (!dayConfig || dayConfig.enabled === false) {
      return [];
    }

    const startTime = dayConfig.startTime || settings.scheduleSettings.startTime || '08:00';
    const endTime = dayConfig.endTime || settings.scheduleSettings.endTime || '18:00';
    const slotDuration = settings.scheduleSettings.slotDuration || 30;
    const breakTimes = dayConfig.breakTimes || [];

    const slots = [];
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);

    let currentTime = startHour * 60 + startMin;
    const endTimeMinutes = endHour * 60 + endMin;

    while (currentTime < endTimeMinutes) {
      const hours = Math.floor(currentTime / 60);
      const minutes = currentTime % 60;
      const timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;

      // Verificar se está dentro de um intervalo
      const currentBreak = breakTimes.find(breakTime => {
        const [breakStartHour, breakStartMin] = breakTime.startTime.split(':').map(Number);
        const [breakEndHour, breakEndMin] = breakTime.endTime.split(':').map(Number);
        const breakStartMinutes = breakStartHour * 60 + breakStartMin;
        const breakEndMinutes = breakEndHour * 60 + breakEndMin;
        return currentTime >= breakStartMinutes && currentTime < breakEndMinutes;
      });

      if (currentBreak) {
        // Adicionar apenas uma vez o slot de intervalo no início
        if (!slots.some(s => s.isBreak && s.breakStart === currentBreak.startTime)) {
          slots.push({
            time: timeStr,
            isBreak: true,
            breakStart: currentBreak.startTime,
            breakEnd: currentBreak.endTime,
            breakDescription: currentBreak.description || 'Intervalo'
          });
        }
        
        // Pular para o fim do intervalo
        const [breakEndHour, breakEndMin] = currentBreak.endTime.split(':').map(Number);
        currentTime = breakEndHour * 60 + breakEndMin;
      } else {
        slots.push({ time: timeStr, isBreak: false });
        currentTime += slotDuration;
      }
    }

    return slots;
  }, [settings, selectedDate]);

  const formatDate = (date) => {
    return date.toLocaleDateString('pt-BR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatDateShort = (date) => {
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const isSameDay = (date1, date2) => {
    return date1.getDate() === date2.getDate() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getFullYear() === date2.getFullYear();
  };

  // Gerar dias do calendário
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDay = firstDay.getDay();
    const days = [];

    // Dias do mês anterior
    for (let i = 0; i < startDay; i++) {
      const date = new Date(year, month, -startDay + i + 1);
      days.push({ date, isCurrentMonth: false });
    }

    // Dias do mês atual
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const date = new Date(year, month, i);
      days.push({ date, isCurrentMonth: true });
    }

    // Completar semana
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      const date = new Date(year, month + 1, i);
      days.push({ date, isCurrentMonth: false });
    }

    return days;
  }, [currentMonth]);

  // Estatísticas do dia
  const stats = useMemo(() => {
    const selectedDateStr = getLocalDateInputValue(selectedDate);
    const dayAppointments = appointments.filter(a => a.date === selectedDateStr);
    
    const scheduled = dayAppointments.filter(a => a.status === 'scheduled').length;
    const confirmed = dayAppointments.filter(a => a.status === 'confirmed').length;
    const present = dayAppointments.filter(a => a.status === 'present').length;
    const absent = dayAppointments.filter(a => a.status === 'absent').length;
    const cancelled = dayAppointments.filter(a => a.status === 'cancelled').length;
    const total = dayAppointments.length;

    const free = timeSlots.filter(s => !s.isBreak && !appointments.find(a => a.time === s.time && a.date === selectedDateStr)).length;
    return { scheduled, confirmed, present, absent, cancelled, total, free };
  }, [appointments, selectedDate]);

  const getAppointmentForTime = (time) => {
    const selectedDateStr = getLocalDateInputValue(selectedDate);
    return appointments.find(apt => apt.time === time && apt.date === selectedDateStr);
  };

  const STATUS_TOKENS = {
    scheduled:  { label: 'Agendado',   color: '#B07A2F', bg: '#F5E9D1', dot: '#D9A24A' },
    confirmed:  { label: 'Confirmado', color: '#2F6FB0', bg: '#DEEAF6', dot: '#5A92CC' },
    present:    { label: 'Presente',   color: '#3D8068', bg: '#E6F1EB', dot: '#3D8068' },
    absent:     { label: 'Falta',      color: '#9F4A60', bg: '#FBE0E5', dot: '#C97A8F' },
    cancelled:  { label: 'Cancelado',  color: '#6E6E73', bg: '#F2F2F7', dot: '#8E8E93' },
  };

  const getStatusToken = (status) => STATUS_TOKENS[status] || { label: '', color: '#8E8E93', bg: '#F2F2F7', dot: '#8E8E93' };
  const getStatusColor = (status) => getStatusToken(status).color;
  const getStatusLabel = (status) => getStatusToken(status).label;

  const getLastVisitInfo = (patientId) => {
    const patient = patients.find(p => String(p._id || p.id) === String(patientId?._id || patientId));
    if (!patient) return null;
    const consultations = Array.isArray(patient.consultations) ? patient.consultations : [];
    let last = null;
    consultations.forEach((c) => {
      if (!c?.date) return;
      const dateStr = String(c.date).split('T')[0];
      const [y, m, d] = dateStr.split('-').map(Number);
      if (!y || !m || !d) return;
      const dt = new Date(y, m - 1, d);
      if (!last || dt > last) last = dt;
    });
    if (!last) return null;
    const dias = Math.floor((Date.now() - last.getTime()) / 86400000);
    const label = `${String(last.getDate()).padStart(2,'0')}/${String(last.getMonth()+1).padStart(2,'0')}/${last.getFullYear()}`;
    let chipBg = '#F2F2F7', chipFg = '#6E6E73';
    if (dias >= 30) { chipBg = '#FBE0E5'; chipFg = '#9F4A60'; }
    else if (dias >= 14) { chipBg = '#FBEEDF'; chipFg = '#A66730'; }
    return { label, dias, chipBg, chipFg };
  };

  const getInitials = (name = '') => {
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const navigatePrevDay = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() - 1);
    setSelectedDate(d);
    setCurrentMonth(new Date(d.getFullYear(), d.getMonth(), 1));
  };

  const navigateNextDay = () => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + 1);
    setSelectedDate(d);
    setCurrentMonth(new Date(d.getFullYear(), d.getMonth(), 1));
  };

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  const handleOpenModal = (presetTime = null) => {
    setAppointmentForm({
      date: getLocalDateInputValue(selectedDate),
      time: presetTime || '09:00',
      patientId: '',
      type: 'Consulta'
    });
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setAppointmentForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveAppointment = async (e) => {
    e.preventDefault();
    
    const patient = patients.find(p => (p._id || p.id) === appointmentForm.patientId);
    if (!patient) {
      setToast({ message: 'Por favor, selecione um paciente', type: 'warning' });
      return;
    }

    try {
      const appointmentData = {
        date: appointmentForm.date,
        time: appointmentForm.time,
        patientId: appointmentForm.patientId,
        patientName: patient.name,
        type: appointmentForm.type
      };

      const response = await api.post('/appointments', appointmentData);
      setAppointments(prev => [...prev, response.data]);
      setShowModal(false);
      setToast({ message: 'Agendamento criado com sucesso!', type: 'success' });
    } catch (error) {
      console.error('Erro ao salvar agendamento:', error);
      const message = error.response?.data?.message || 'Erro ao criar agendamento';
      setToast({ message, type: 'error' });
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (!window.confirm('Excluir este agendamento?')) return;
    try {
      await api.delete(`/appointments/${appointmentId}`);
      setAppointments(prev => prev.filter(a => a._id !== appointmentId));
      setToast({ message: 'Agendamento excluído.', type: 'success' });
    } catch (error) {
      console.error('Erro ao excluir agendamento:', error);
      setToast({ message: 'Erro ao excluir agendamento', type: 'error' });
    }
  };

  const handleUpdateStatus = async (appointmentId, newStatus) => {
    try {
      await api.put(`/appointments/${appointmentId}`, { status: newStatus });
      setAppointments(prev => 
        prev.map(apt => 
          apt._id === appointmentId ? { ...apt, status: newStatus } : apt
        )
      );
      setShowStatusMenu(null);
      setToast({ message: 'Status atualizado com sucesso!', type: 'success' });
    } catch (error) {
      console.error('Erro ao atualizar status:', error);
      setToast({ message: 'Erro ao atualizar status', type: 'error' });
    }
  };

  // Slots efetivos: slots configurados + agendamentos fora dos slots (horários avulsos)
  const selectedDateStr = getLocalDateInputValue(selectedDate);
  const effectiveSlots = useMemo(() => {
    const dayAppointments = appointments.filter(a => a.date === selectedDateStr);
    const orphaned = dayAppointments
      .filter(a => !timeSlots.some(s => !s.isBreak && s.time === a.time))
      .map(a => ({ time: a.time, isBreak: false }));
    const merged = [...timeSlots, ...orphaned];
    merged.sort((a, b) => a.time.localeCompare(b.time));
    return merged;
  }, [timeSlots, appointments, selectedDateStr]);

  // Group slots by period
  const slotGroups = useMemo(() => {
    const groups = [
      { label: 'Manhã', filter: (s) => parseInt(s.time) < 13 },
      { label: 'Tarde', filter: (s) => !s.isBreak && parseInt(s.time) >= 13 },
    ];
    return groups.map(g => ({ ...g, slots: effectiveSlots.filter(g.filter) })).filter(g => g.slots.length > 0);
  }, [effectiveSlots]);

  return (
    <div className="patients-card">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div className="patients-content" style={{ paddingTop: 44, paddingBottom: 56 }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: '#FBE0E5', color: '#9F4A60', padding: '4px 10px', borderRadius: 999, fontSize: 11, letterSpacing: '-0.005em', fontWeight: 600, whiteSpace: 'nowrap' }}>
              <span style={{ width: 5, height: 5, borderRadius: 999, background: '#C97A8F' }} />
              Agenda da clínica
            </div>
            <h1 style={{ fontWeight: 700, fontSize: 38, lineHeight: 1.18, letterSpacing: '-0.035em', margin: '12px 0 0', color: '#1C1C1E' }}>Agenda</h1>
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
              <button onClick={navigatePrevDay} style={navArrowBtn} title="Dia anterior">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
              </button>
              <div style={{ fontSize: 14, color: '#6E6E73', letterSpacing: '-0.005em' }}>
                {formatDate(selectedDate)}{isToday(selectedDate) && <span style={{ color: '#9F4A60', fontWeight: 600 }}> · Hoje</span>}
              </div>
              <button onClick={navigateNextDay} style={navArrowBtn} title="Próximo dia">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
              </button>
              {!isToday(selectedDate) && (
                <button onClick={() => { setSelectedDate(new Date()); setCurrentMonth(new Date()); }} style={{ fontFamily: 'inherit', fontSize: 12, fontWeight: 600, letterSpacing: '-0.005em', padding: '5px 10px', borderRadius: 8, border: '1px solid #E5E5EA', background: '#FFFFFF', color: '#1C1C1E', cursor: 'pointer', whiteSpace: 'nowrap' }}>Hoje</button>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0, marginTop: 6 }}>
            <button className="mac-button" onClick={handleOpenModal}>+ Novo agendamento</button>
          </div>
        </div>

        {/* Search */}
        <div style={{ marginTop: 28, position: 'relative' }}>
          <MdSearch style={{ position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', color: '#8E8E93', pointerEvents: 'none' }} size={16} />
          <input
            type="text"
            placeholder="Buscar paciente ou procedimento…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: '100%', fontFamily: 'inherit', fontSize: 14, letterSpacing: '-0.005em', padding: '12px 16px 12px 42px', border: '1px solid #E5E5EA', borderRadius: 14, background: '#FAFAFB', color: '#1C1C1E', outline: 'none', boxSizing: 'border-box' }}
          />
        </div>

        {/* Two-column grid */}
        <div style={{ marginTop: 24, display: 'grid', gridTemplateColumns: '1fr 380px', gap: 24, alignItems: 'start' }}>

          {/* LEFT: Horários do dia */}
          <div style={{ background: '#FFFFFF', border: '1px solid #E5E5EA', borderRadius: 22, boxShadow: '0 1px 2px rgba(28,28,30,0.04), 0 12px 32px -22px rgba(28,28,30,0.12)', overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', borderBottom: '1px solid #F2F2F7' }}>
              <div>
                <h2 style={{ fontWeight: 700, fontSize: 18, margin: 0, letterSpacing: '-0.022em', color: '#1C1C1E' }}>Horários do dia</h2>
                <div style={{ marginTop: 3, fontSize: 12.5, color: '#6E6E73', letterSpacing: '-0.005em' }}>
                  {stats.total} {stats.total === 1 ? 'atendimento' : 'atendimentos'} · {stats.free} {stats.free === 1 ? 'horário livre' : 'horários livres'}
                </div>
              </div>
            </div>

            {loading ? (
              <div style={{ padding: 32, textAlign: 'center', color: '#8E8E93' }}>Carregando agendamentos…</div>
            ) : effectiveSlots.length === 0 ? (
              <div style={{ padding: '48px 32px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                <MdAccessTime size={40} style={{ color: '#C7C7CC' }} />
                <p style={{ color: '#8E8E93', margin: 0, fontSize: 14 }}>Sem atendimento neste dia.<br />Configure os dias de atendimento em Configurações.</p>
              </div>
            ) : (
              <div style={{ padding: '6px 12px 12px' }}>
                {slotGroups.map((group) => (
                  <div key={group.label} style={{ marginTop: 6 }}>
                    <div style={{ padding: '10px 14px 6px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8E8E93' }}>
                      {group.label} <span style={{ marginLeft: 6, color: '#C7C7CC', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>{group.slots.length} slots</span>
                    </div>
                    {group.slots.map((slot, i) => {
                      if (slot.isBreak) {
                        return (
                          <div key={`break-${slot.time}`} style={{ display: 'grid', gridTemplateColumns: '4px 82px 1fr', gap: 12, alignItems: 'center', padding: '12px 10px 12px 0', borderTop: i === 0 ? 'none' : '1px solid #F2F2F7' }}>
                            <div style={{ width: 3, height: 28, borderRadius: 999, marginLeft: 6, background: '#D9A24A' }} />
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#8E8E93', letterSpacing: '-0.005em', fontVariantNumeric: 'tabular-nums' }}>{slot.time}</div>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 9px', borderRadius: 999, background: '#F5E9D1', color: '#B07A2F', fontSize: 11, fontWeight: 600, letterSpacing: '-0.005em', width: 'fit-content' }}>
                              <MdFreeBreakfast size={11} />{slot.breakDescription}
                            </span>
                          </div>
                        );
                      }
                      const appointment = getAppointmentForTime(slot.time);
                      const filtered = searchQuery && appointment && !appointment.patientName?.toLowerCase().includes(searchQuery.toLowerCase()) && !appointment.type?.toLowerCase().includes(searchQuery.toLowerCase());
                      if (filtered) return null;
                      const tok = appointment ? getStatusToken(appointment.status) : null;
                      return (
                        <div
                          key={slot.time}
                          onClick={() => !appointment && handleOpenModal(slot.time)}
                          style={{ display: 'grid', gridTemplateColumns: '4px 82px 1fr auto 32px', gap: 12, alignItems: 'center', padding: '12px 10px 12px 0', borderTop: i === 0 ? 'none' : '1px solid #F2F2F7', cursor: appointment ? 'default' : 'pointer', borderRadius: 4, transition: 'background 0.12s', background: 'transparent' }}
                          onMouseEnter={e => { e.currentTarget.style.background = '#FAFAFB'; const h = e.currentTarget.querySelector('.add-hint'); if (h) h.style.opacity = '1'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; const h = e.currentTarget.querySelector('.add-hint'); if (h) h.style.opacity = '0'; }}
                        >
                          <div style={{ width: 3, height: 28, borderRadius: 999, marginLeft: 6, background: appointment ? tok.dot : 'transparent' }} />
                          <div style={{ fontSize: 14, fontWeight: 600, color: appointment ? '#1C1C1E' : '#8E8E93', letterSpacing: '-0.005em', fontVariantNumeric: 'tabular-nums' }}>{slot.time}</div>

                          {appointment ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                              <div style={{ width: 30, height: 30, borderRadius: 999, background: 'linear-gradient(160deg, #EFE3F0 0%, #DECCDE 100%)', color: '#7A4A86', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontWeight: 700, fontSize: 11.5, letterSpacing: '-0.005em' }}>
                                {getInitials(appointment.patientName || '')}
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: '#1C1C1E', letterSpacing: '-0.005em', lineHeight: 1.2 }}>{toTitleCase(appointment.patientName || '')}</div>
                                <div style={{ fontSize: 12, color: '#8E8E93', marginTop: 2, letterSpacing: '-0.005em' }}>{appointment.type}</div>
                                {(() => {
                                  const lv = getLastVisitInfo(appointment.patientId);
                                  if (!lv) return null;
                                  return (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                                      <span style={{ fontSize: 11, color: '#8E8E93', letterSpacing: '-0.005em', fontVariantNumeric: 'tabular-nums' }}>{lv.label}</span>
                                      <span style={{ fontSize: 10.5, fontWeight: 600, color: lv.chipFg, background: lv.chipBg, padding: '1px 7px', borderRadius: 999, letterSpacing: '-0.005em', whiteSpace: 'nowrap' }}>há {lv.dias} {lv.dias === 1 ? 'dia' : 'dias'}</span>
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          ) : (
                            <div style={{ fontSize: 13, color: '#8E8E93', letterSpacing: '-0.005em', fontStyle: 'italic' }}>
                              Disponível
                            </div>
                          )}

                          {appointment ? (
                            <div style={{ position: 'relative' }}>
                              <span
                                onClick={(e) => { e.stopPropagation(); setShowStatusMenu(showStatusMenu === appointment._id ? null : appointment._id); }}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, color: tok.color, background: tok.bg, padding: '4px 10px', borderRadius: 999, letterSpacing: '-0.005em', whiteSpace: 'nowrap', cursor: 'pointer' }}
                              >
                                <span style={{ width: 5, height: 5, borderRadius: 999, background: tok.dot }} />
                                {tok.label}
                              </span>
                              {showStatusMenu === appointment._id && (
                                <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 8, background: 'rgba(255,255,255,0.98)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: 12, boxShadow: '0 8px 32px rgba(0,0,0,0.15)', border: '1px solid rgba(0,0,0,0.08)', overflow: 'hidden', zIndex: 1000, minWidth: 170 }} onClick={e => e.stopPropagation()}>
                                  {Object.entries(STATUS_TOKENS).map(([key, t]) => (
                                    <div key={key} onClick={() => handleUpdateStatus(appointment._id, key)} style={{ padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10, background: appointment.status === key ? t.bg : 'transparent', transition: 'background 0.15s' }} onMouseEnter={e => { if (appointment.status !== key) e.currentTarget.style.background = '#FAFAFB'; }} onMouseLeave={e => { if (appointment.status !== key) e.currentTarget.style.background = 'transparent'; }}>
                                      <div style={{ width: 7, height: 7, borderRadius: 999, background: t.dot }} />
                                      <span style={{ fontSize: '0.9rem', color: '#1C1C1E', fontWeight: 500 }}>{t.label}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="add-hint" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: '#9F4A60', letterSpacing: '-0.005em', whiteSpace: 'nowrap', opacity: 0, transition: 'opacity 0.12s' }}>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
                              Agendar
                            </span>
                          )}

                          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            {appointment ? (
                              <button key={`del-${appointment._id}`} onClick={(e) => { e.stopPropagation(); handleDeleteAppointment(appointment._id); }} title="Excluir" style={{ width: 28, height: 28, borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#C7C7CC', padding: 0, transition: 'color 0.15s' }} onMouseEnter={e => e.currentTarget.style.color = '#FF3B30'} onMouseLeave={e => e.currentTarget.style.color = '#C7C7CC'}>
                                <MdDelete size={15} />
                              </button>
                            ) : (
                              <button key={`add-${slot.time}`} onClick={() => handleOpenModal(slot.time)} style={{ width: 28, height: 28, borderRadius: 8, border: 'none', background: 'transparent', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: '#C7C7CC', padding: 0 }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT: Calendário + Estatísticas */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {/* Calendário */}
            <div style={{ background: '#FFFFFF', border: '1px solid #E5E5EA', borderRadius: 22, boxShadow: '0 1px 2px rgba(28,28,30,0.04), 0 12px 32px -22px rgba(28,28,30,0.12)', padding: '20px 22px 22px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <button onClick={previousMonth} style={navArrowBtn} title="Mês anterior">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                </button>
                <div style={{ fontSize: 14, fontWeight: 600, letterSpacing: '-0.012em', color: '#1C1C1E' }}>
                  {currentMonth.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                </div>
                <button onClick={nextMonth} style={navArrowBtn} title="Próximo mês">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0, marginBottom: 6 }}>
                {['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'].map(d => (
                  <div key={d} style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#8E8E93', textAlign: 'center', padding: '4px 0' }}>{d}</div>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', rowGap: 4 }}>
                {calendarDays.map(({ date, isCurrentMonth }, index) => {
                  const selected = isSameDay(date, selectedDate);
                  const today = isToday(date);
                  return (
                    <div key={index} style={{ display: 'flex', justifyContent: 'center', padding: '2px 0' }}>
                      <button
                        onClick={() => setSelectedDate(date)}
                        style={{ width: 30, height: 30, borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12.5, fontWeight: selected || today ? 700 : 500, letterSpacing: '-0.005em', fontVariantNumeric: 'tabular-nums', color: selected ? '#FFFFFF' : !isCurrentMonth ? '#C7C7CC' : '#1C1C1E', background: selected ? '#9F4A60' : today ? '#FBE0E5' : 'transparent', border: 'none', cursor: 'pointer', boxShadow: selected ? '0 1px 2px rgba(159,74,96,0.18), 0 6px 14px -8px rgba(159,74,96,0.32)' : 'none', transition: 'background 0.12s' }}
                      >
                        {date.getDate()}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Estatísticas */}
            <div style={{ background: '#FFFFFF', border: '1px solid #E5E5EA', borderRadius: 22, boxShadow: '0 1px 2px rgba(28,28,30,0.04), 0 12px 32px -22px rgba(28,28,30,0.12)', padding: '20px 22px 14px' }}>
              <h3 style={{ fontWeight: 700, fontSize: 15, margin: 0, letterSpacing: '-0.018em', color: '#1C1C1E' }}>Estatísticas do dia</h3>
              <div style={{ marginTop: 4, fontSize: 12, color: '#8E8E93', letterSpacing: '-0.005em' }}>Status atual dos atendimentos</div>
              <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column' }}>
                {[
                  { label: 'Total',      value: stats.total,     color: '#1C1C1E', dot: '#8E8E93' },
                  { label: 'Agendados',  value: stats.scheduled, color: STATUS_TOKENS.scheduled.color,  dot: STATUS_TOKENS.scheduled.dot },
                  { label: 'Confirmados',value: stats.confirmed, color: STATUS_TOKENS.confirmed.color,  dot: STATUS_TOKENS.confirmed.dot },
                  { label: 'Presentes',  value: stats.present,   color: STATUS_TOKENS.present.color,    dot: STATUS_TOKENS.present.dot },
                  { label: 'Faltas',     value: stats.absent,    color: STATUS_TOKENS.absent.color,     dot: STATUS_TOKENS.absent.dot },
                  { label: 'Cancelados', value: stats.cancelled, color: STATUS_TOKENS.cancelled.color,  dot: STATUS_TOKENS.cancelled.dot },
                ].map((s, i) => (
                  <div key={s.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderTop: i === 0 ? 'none' : '1px solid #F2F2F7' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                      <span style={{ width: 7, height: 7, borderRadius: 999, background: s.dot, flexShrink: 0 }} />
                      <span style={{ fontSize: 13, color: '#1C1C1E', fontWeight: 500, letterSpacing: '-0.005em' }}>{s.label}</span>
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: s.color, letterSpacing: '-0.018em', fontVariantNumeric: 'tabular-nums' }}>{s.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Modal de Novo Agendamento */}
        {showModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '24px'
          }} onClick={handleCloseModal}>
            <div 
              className="form-card" 
              style={{ 
                maxWidth: '500px', 
                width: '100%',
                maxHeight: '90vh',
                overflow: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, letterSpacing: '-0.022em' }}>Novo Agendamento</h2>
                <button onClick={handleCloseModal} style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid #E5E5EA', background: '#FFFFFF', color: '#6E6E73', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, padding: 0 }}>×</button>
              </div>

              <form onSubmit={handleSaveAppointment} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#1C1C1E', letterSpacing: '-0.005em' }}>Data do Agendamento</span>
                  <input className="mac-input" type="date" name="date" value={appointmentForm.date} onChange={handleFormChange} required style={{ fontSize: 13 }} />
                </label>

                <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#1C1C1E', letterSpacing: '-0.005em' }}>Horário</span>
                  <select className="mac-input" name="time" value={appointmentForm.time} onChange={handleFormChange} required style={{ fontSize: 13 }}>
                    {timeSlots.filter(slot => !slot.isBreak).map(slot => (
                      <option key={slot.time} value={slot.time}>{slot.time}</option>
                    ))}
                  </select>
                </label>

                <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#1C1C1E', letterSpacing: '-0.005em' }}>Paciente</span>
                  <select className="mac-input" name="patientId" value={appointmentForm.patientId} onChange={handleFormChange} required style={{ fontSize: 13 }}>
                    <option value="">Selecione um paciente</option>
                    {patients.map(patient => (
                      <option key={patient._id || patient.id} value={patient._id || patient.id}>{patient.name}</option>
                    ))}
                  </select>
                </label>

                <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: '#1C1C1E', letterSpacing: '-0.005em' }}>Tipo de Atendimento</span>
                  <select className="mac-input" name="type" value={appointmentForm.type} onChange={handleFormChange} required style={{ fontSize: 13 }}>
                    <option value="Consulta">Consulta</option>
                    <option value="Retorno">Retorno</option>
                    <option value="Procedimento">Procedimento</option>
                    <option value="Avaliação">Avaliação</option>
                  </select>
                </label>

                <div style={{ display: 'flex', gap: '12px', marginTop: '8px', justifyContent: 'flex-end' }}>
                  <button type="button" className="ghost-button" onClick={handleCloseModal}>
                    Cancelar
                  </button>
                  <button type="submit" className="mac-button">
                    Agendar
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
